import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { updateCartItem } from '@homeplay/app';
import { LOTTO_CONSTANTS } from '../../constants';
import { calculatePotentialPayout, formatBalance, formatOdds } from '../../utils/common';
import { calculateCombinations } from '../../utils/marketTypeHelpers';
import { useStakeInput } from '../../hooks/useStakeInput';
import { useBallCounts } from '../../hooks/useBallCounts';
import { useCountdownTimer } from '../../hooks/useCountdownTimer';
import StakeInput from '../StakeInput';
import BallsDisplay from '../BallsDisplay';
import BallEditDrawer from '../BallEditDrawer';
import trashIcon from '../../../../../../shared/assets/images/lotto-assistant/trash.svg';
import minusIcon from '../../../../../../shared/assets/images/lotto-assistant/minus-circle.svg';
import plusIcon from '../../../../../../shared/assets/images/lotto-assistant/plus-circle.svg';
import classes from './CartItem.module.scss';

const CartItem = ({ item, totalBalance, onRemove, declineError }) => {
  const [editedNumbers, setEditedNumbers] = useState(item.balls ?? []);
  const [isBallDrawerOpen, setIsBallDrawerOpen] = useState(false);
  const [hasBallDrawerMounted, setHasBallDrawerMounted] = useState(false);
  const [editedRepeatForDraws, setEditedRepeatForDraws] = useState(item.repeat_for_draws ?? 1);
  const [editedAcceptOddsChanges, setEditedAcceptOddsChanges] = useState(
    item.change_odds_agreement ?? true
  );

  const dispatch = useDispatch();

  const minStake = LOTTO_CONSTANTS.MIN_STAKE;
  const maxStake = totalBalance / 100;
  const hasBalls = item.balls != null;
  const initialStake = item.stake / LOTTO_CONSTANTS.CENTS_MULTIPLIER;

  const {
    minNormalCount,
    maxNormalCount,
    requiredBonusCount,
    singleBetsCount,
  } = useBallCounts({
    hasBalls,
    marketType: item.market_type,
    balls: item.balls,
    mainGridTotal: item.main_grid_total,
    editedNumbers,
  });

  const {
    stake: editedStake,
    stakeInput: editedStakeInput,
    handleChange: handleStakeChange,
    stopEditing: stopStakeEdit,
  } = useStakeInput({
    initialStake,
    minStake,
    maxStake,
    onBlur: (stake) => {
      dispatch(
        updateCartItem({
          id: item.id,
          updates: {
            stake: Math.round(stake * LOTTO_CONSTANTS.CENTS_MULTIPLIER),
            to_pay: Math.round(stake * singleBetsCount * LOTTO_CONSTANTS.CENTS_MULTIPLIER),
            potential_payout: calculatePotentialPayout(
              stake,
              item.outcome.odds,
              singleBetsCount,
              editedRepeatForDraws
            ),
          },
        })
      );
    },
  });

  const handleBallDrawerConfirm = useCallback(
    (numbers) => {
      setEditedNumbers(numbers);

      const normalCount = numbers.filter((n) => n.type === 'NORMAL').length;
      const newSingleBetsCount =
        hasBalls && minNormalCount > 0 && normalCount >= minNormalCount
          ? calculateCombinations(normalCount, minNormalCount)
          : 1;

      const newPotentialPayout = calculatePotentialPayout(
        editedStake,
        item.outcome.odds,
        newSingleBetsCount,
        editedRepeatForDraws
      );

      dispatch(
        updateCartItem({
          id: item.id,
          updates: {
            balls: numbers,
            single_bets_count: newSingleBetsCount,
            to_pay: Math.round(editedStake * newSingleBetsCount * LOTTO_CONSTANTS.CENTS_MULTIPLIER),
            potential_payout: newPotentialPayout,
          },
        })
      );
    },
    [dispatch, editedStake, editedRepeatForDraws, hasBalls, item.id, item.outcome.odds, minNormalCount]
  );

  const handleDrawsChange = (draws) => {
    setEditedRepeatForDraws(draws);

    const shouldResetOddsChanges = draws === 1;
    if (shouldResetOddsChanges) setEditedAcceptOddsChanges(true);

    const newPotentialPayout = calculatePotentialPayout(
      editedStake,
      item.outcome.odds,
      singleBetsCount,
      draws
    );

    dispatch(
      updateCartItem({
        id: item.id,
        updates: {
          repeat_for_draws: draws,
          change_odds_agreement: shouldResetOddsChanges ? true : editedAcceptOddsChanges,
          potential_payout: newPotentialPayout,
        },
      })
    );
  };

  const handleOddsChangesChange = (accept) => {
    setEditedAcceptOddsChanges(accept);
    dispatch(updateCartItem({ id: item.id, updates: { change_odds_agreement: accept } }));
  };

  const potentialPayout = useMemo(
    () =>
      calculatePotentialPayout(
        editedStake,
        item.outcome.odds,
        singleBetsCount,
        editedRepeatForDraws
      ),
    [editedStake, item.outcome.odds, singleBetsCount, editedRepeatForDraws]
  );

  const { isExpired } = useCountdownTimer({ targetDate: item.draw_date });

  useEffect(() => {
    if (isExpired && !item.isExpired) {
      dispatch(updateCartItem({ id: item.id, updates: { isExpired: true } }));
    }
  }, [isExpired, item.isExpired, item.id, dispatch]);

  const handleBallDrawerClose = useCallback(() => setIsBallDrawerOpen(false), []);

  useEffect(() => {
    if (isBallDrawerOpen) setHasBallDrawerMounted(true);
  }, [isBallDrawerOpen]);

  return (
    <div className={classes.Item + (isExpired ? ' ' + classes.Expired : '')}>
      <div className={classes.Header}>
        <span className={classes.LotteryName}>{item.lottery_name}</span>
        <button
          type="button"
          onClick={() => onRemove(item.id)}
          className={classes.RemoveButton}
          aria-label="Remove from cart"
        >
          <img src={trashIcon} alt="" />
        </button>
      </div>

      <p className={classes.Subtitle}>{item.market_title}</p>

      <hr className={classes.Divider} />

      {hasBalls && (
        <div className={classes.BallsSection}>
          <BallsDisplay
            numbers={editedNumbers}
            size="medium"
            showEditButton
            onEditClick={() => setIsBallDrawerOpen(true)}
          />
        </div>
      )}

      {!hasBalls && (
        <div className={classes.BallsSection}>
          <span className={classes.Label}>OUTCOME</span>
          <span className={classes.OddsValue}>{item.outcome.outcome_title}</span>
        </div>
      )}

      <hr className={classes.Divider} />

      <div className={classes.DrawsCounterContainer}>
        <span className={classes.Label}>NUMBER OF DRAWS</span>
        <div className={classes.DrawsCounter}>
          <button
            type="button"
            onClick={() => handleDrawsChange(Math.max(1, editedRepeatForDraws - 1))}
            disabled={editedRepeatForDraws <= 1}
            className={classes.CounterButton}
            aria-label="Decrease draws"
          >
            <img src={minusIcon} alt="" />
          </button>
          <span className={classes.CounterValue}>{editedRepeatForDraws}</span>
          <button
            type="button"
            onClick={() => handleDrawsChange(Math.min(10, editedRepeatForDraws + 1))}
            disabled={editedRepeatForDraws >= 10}
            className={classes.CounterButton}
            aria-label="Increase draws"
          >
            <img src={plusIcon} alt="" />
          </button>
        </div>
      </div>

      {editedRepeatForDraws > 1 && (
        <label className={classes.OddsCheckbox}>
          <input
            type="checkbox"
            checked={editedAcceptOddsChanges}
            onChange={(e) => handleOddsChangesChange(e.target.checked)}
          />
          <span>Accept odds changes</span>
        </label>
      )}

      <div className={classes.StakeOddsRow}>
        <StakeInput
          stakeInput={editedStakeInput}
          onInputChange={handleStakeChange}
          onBlur={stopStakeEdit}
        />
        <div className={classes.OddsGroup}>
          <span className={classes.Label}>ODDS</span>
          <span className={classes.OddsValue}>{formatOdds(item.outcome.odds)}</span>
        </div>
      </div>

      <div className={classes.SummaryBand + (declineError ? ' ' + classes.SummaryBandError : '')}>
        <span className={classes.Label}>POTENTIAL PAYOUT</span>
        <span className={classes.SummaryValue}>{formatBalance(potentialPayout, 'ZAR')}</span>
      </div>

      {declineError && <p className={classes.DeclineError}>{declineError}</p>}

      {hasBalls && hasBallDrawerMounted && (
        <BallEditDrawer
          open={isBallDrawerOpen}
          onClose={handleBallDrawerClose}
          onConfirm={handleBallDrawerConfirm}
          selectedNumbers={editedNumbers}
          mainGridTotal={item.main_grid_total}
          secondaryGridTotal={item.secondary_grid_total}
          secondaryUnique={item.secondary_unique}
          minNormalCount={minNormalCount}
          maxNormalCount={maxNormalCount}
          requiredBonusCount={requiredBonusCount}
          ticket={item}
        />
      )}

      {isExpired && (
        <div className={classes.ExpiredOverlay}>
          <span className={classes.ExpiredWatermark}>Closed</span>
        </div>
      )}
    </div>
  );
};

export default CartItem;
